import type { Call, ShipState } from "@cruise/core";

/** "just now", "12 min ago", "3 h ago", "2 d ago". */
export function minutesAgo(minutes: number | null): string {
  if (minutes === null) return "never";
  if (minutes < 1) return "just now";
  if (minutes < 60) return `${minutes} min ago`;
  if (minutes < 48 * 60) return `${Math.round(minutes / 60)} h ago`;
  return `${Math.round(minutes / 1440)} d ago`;
}

export function sinceIso(iso: string | null, now: number): string {
  return iso ? minutesAgo(Math.max(0, Math.round((now - Date.parse(iso)) / 60_000))) : "never";
}

/** "Mon 14 Sep" in the port's time zone. */
export function portDate(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-GB", { timeZone, weekday: "short", day: "numeric", month: "short" }).format(
    new Date(iso),
  );
}

/** "06:30" in the port's time zone. */
export function portTime(iso: string, timeZone: string): string {
  return new Intl.DateTimeFormat("en-GB", { timeZone, hour: "2-digit", minute: "2-digit", hourCycle: "h23" }).format(
    new Date(iso),
  );
}

/** "06:30–17:00", "arrives 06:30", or "date only" for sources without times. */
export function callTimes(call: Call, timeZone: string): string {
  if (call.dateOnly) return "time not published";
  if (call.arrival && call.departure) return `${portTime(call.arrival, timeZone)}–${portTime(call.departure, timeZone)}`;
  if (call.arrival) return `arrives ${portTime(call.arrival, timeZone)}`;
  if (call.departure) return `departs ${portTime(call.departure, timeZone)}`;
  return "";
}

/** Actual times reported by the port authority: "arrived 07:20 · left 18:06", or "" when none. */
export function actualTimes(call: Call, timeZone: string): string {
  const parts: string[] = [];
  if (call.actualArrival) parts.push(`arrived ${portTime(call.actualArrival, timeZone)}`);
  if (call.actualDeparture) parts.push(`left ${portTime(call.actualDeparture, timeZone)}`);
  return parts.join(" · ");
}

export function callStart(call: Call): string {
  return (call.arrival ?? call.departure)!;
}

/** "14 Sep 07:12" in UTC. */
export function utcDayTime(iso: string): string {
  return new Intl.DateTimeFormat("en-GB", {
    timeZone: "UTC",
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).format(new Date(iso));
}

export function utcClock(now: Date): string {
  return `${now.toISOString().slice(11, 19)} UTC`;
}

export function thousands(n: number): string {
  return n.toLocaleString("en-US");
}

export function compact(n: number): string {
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n);
}

export function stateLabel(state: ShipState): string {
  switch (state) {
    case "under-way":
      return "UNDER WAY";
    case "stopped":
      return "STOPPED";
    case "no-recent-signal":
      return "NO RECENT AIS";
    case "never-seen":
      return "NOT SEEN YET";
  }
}

/** Chip class for a ship state; the colours match the ship dots on the map. */
export function stateChip(state: ShipState): "moving" | "stopped" | "no-signal" | "dim" {
  switch (state) {
    case "under-way":
      return "moving";
    case "stopped":
      return "stopped";
    case "no-recent-signal":
      return "no-signal";
    case "never-seen":
      return "dim";
  }
}

export function knots(sog: number | null): string {
  return sog === null ? "—" : `${sog.toFixed(1)} kn`;
}

export function degrees(value: number | null): string {
  return value === null ? "—" : `${String(Math.round(value)).padStart(3, "0")}°`;
}

export function latLon(lat: number, lon: number): string {
  return `${Math.abs(lat).toFixed(2)}°${lat >= 0 ? "N" : "S"} ${Math.abs(lon).toFixed(2)}°${lon >= 0 ? "E" : "W"}`;
}
